import { useSignalR } from '@app/src/context/SignalRContext'
import { useEffect, useCallback, useRef } from 'react'

/**
 * SignalR listener hook that supports receiving data.
 * Unlike useSignalRListener which only supports method-only notifications,
 * this hook can receive data payloads from the server.
 *
 * @param methodName - The SignalR method name to listen for
 * @param handler - Callback function that receives the data payload
 * @param enabled - Whether the listener is active (default: true)
 */
function useSignalRDataListener<T>(methodName: string, handler: (data: T) => void, enabled = true): void {
  const { on, off } = useSignalR()
  // Use ref to avoid re-registering listener on every handler change
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const stableHandler = useCallback(
    (...args: unknown[]) => {
      // SignalR passes data as first argument
      const data = args[0] as T
      handlerRef.current(data)
    },
    [methodName],
  )

  useEffect(() => {
    if (!enabled) {
      return
    }

    on(methodName, stableHandler)
    return () => {
      off(methodName, stableHandler)
    }
  }, [on, off, methodName, stableHandler, enabled])
}

export default useSignalRDataListener
